import React, { useEffect, useState } from 'react';
import type { CategoryDto, SubCategoryDto } from '../types/categoryTypes';
import {
  useCreateCategory,
  useUpdateCategory,
} from '../hooks/useCategoryMutations';
import { IconUploadField } from './IconUploadField';
import '../styles/Modal.css';

interface CategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editCategory?: CategoryDto | SubCategoryDto | null;
  categories: CategoryDto[];
}

interface FormErrors {
  nameEn?: string;
  nameHe?: string;
}

export function CategoryFormModal({
  isOpen,
  onClose,
  editCategory,
  categories,
}: CategoryFormModalProps): JSX.Element | null {
  const [nameEn, setNameEn] = useState('');
  const [nameHe, setNameHe] = useState('');
  const [parentId, setParentId] = useState<number | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);

  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const isEdit = editCategory != null;

  useEffect(() => {
    if (isOpen) {
      if (editCategory) {
        setNameEn(editCategory.nameEn ?? '');
        setNameHe(editCategory.nameHe);
        setIconUrl(editCategory.iconUrl);
        setParentId(null);
      } else {
        setNameEn('');
        setNameHe('');
        setParentId(null);
        setIconUrl(null);
      }
      setErrors({});
      setApiError(null);
    }
  }, [isOpen, editCategory]);

  if (!isOpen) return null;

  function validate(): boolean {
    const next: FormErrors = {};
    if (!nameHe.trim()) next.nameHe = 'שדה חובה';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!validate()) return;
    setApiError(null);
    try {
      if (isEdit && editCategory) {
        await updateMutation.mutateAsync({
          id: editCategory.id,
          req: { nameEn: nameEn.trim() || null, nameHe, iconUrl, parentId },
        });
      } else {
        await createMutation.mutateAsync({ nameEn: nameEn.trim() || null, nameHe, iconUrl, parentId });
      }
      onClose();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'אירעה שגיאה.');
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-box">
        <h2 className="modal-title">
          {isEdit ? 'עריכת קטגוריה' : 'הוספת קטגוריה'}
        </h2>
        {apiError && <div className="api-error-message">{apiError}</div>}
        <form className="modal-form" onSubmit={handleSubmit} noValidate>
          <div className="form-field">
            <label className="form-label" htmlFor="nameEn">
              שם (אנגלית)
            </label>
            <input
              id="nameEn"
              type="text"
              className="form-input"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
            />
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="nameHe">
              שם (עברית) *
            </label>
            <input
              id="nameHe"
              type="text"
              className={`form-input${errors.nameHe ? ' form-input--error' : ''}`}
              value={nameHe}
              onChange={(e) => setNameHe(e.target.value)}
              dir="rtl"
            />
            {errors.nameHe && (
              <span className="form-error">{errors.nameHe}</span>
            )}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="parentId">
              קטגוריית אב
            </label>
            <select
              id="parentId"
              className="form-select"
              value={parentId ?? ''}
              onChange={(e) =>
                setParentId(
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
            >
              <option value="">— קטגוריה ראשית —</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nameEn ?? cat.nameHe}
                </option>
              ))}
            </select>
          </div>

          <IconUploadField iconUrl={iconUrl} onIconUrlChange={setIconUrl} />

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isPending}
            >
              ביטול
            </button>
            <button type="submit" className="btn-primary" disabled={isPending}>
              {isPending ? 'שומר...' : isEdit ? 'שמור שינויים' : 'צור'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
